<template>
  <platform-infinite-scroll
    hide-spinner
    :fetching="loading"
    :fetched-all="true"
    :activate-distance="200"
  >
    <v-container>
      <add-organisation-user-modal
        v-if="addingUser"
        :organisations="organisations"
        :existing-organisation-users="organisationUsers"
        @close="addingUser = false"
        @created="userAdded"
      />
      <edit-organisation-user-modal
        v-if="editingOrganisationUser"
        :organisation-user="editingOrganisationUser"
        :organisation-name="getOrganisationName(editingOrganisationUser.organisationId)"
        :organisation-user-count="getOrganisationUsersForOrg(editingOrganisationUser.organisationId).length"
        @close="editingOrganisationUser = null"
        @removed="userRemoved"
      />
      <v-row :class="{ 'flex-column-reverse': $vuetify.breakpoint.mdAndDown }">
        <v-col :cols="12" :lg="8">
          <v-card
            v-for="organisation in organisations"
            :key="organisation.id"
            class="mb-4"
          >
            <v-card-title class="d-flex align-center">
              <platform-avatar
                :entity="organisation"
                entity-type="company"
                :size="40"
                class="mr-3"
              />
              {{ organisation.name }}
            </v-card-title>
            <v-card-text>
              <v-row class="pt-2" no-gutters justify="start">
                <v-col
                  v-for="orgUser in getOrganisationUsersForOrg(organisation.id)"
                  :key="orgUser.id"
                  class="shrink d-flex flex-column justify-start px-3"
                  :style="{ flexBasis: '180px' }"
                >
                  <platform-entity-card
                    hover
                    flat
                    bubble-edit
                    placeholder-size="xl"
                    align="center"
                    class="text-body-2"
                    entity-type="user"
                    :inner-margin="3"
                    :entity="formatUserEntity(orgUser)"
                    :avatar-size="48"
                    :inline-header="false"
                    @edit="editingOrganisationUser = orgUser"
                    @click="editingOrganisationUser = orgUser"
                  >
                    <span class="grey--text text--darken-1" style="font-size: 0.85em">
                      {{ formatDate(orgUser.createdAt) }}
                    </span>
                  </platform-entity-card>
                </v-col>
                <v-col
                  v-if="getOrganisationUsersForOrg(organisation.id).length === 0"
                  cols="12"
                  class="text-center grey--text py-4"
                >
                  {{ $t('page.organisationUsers.noUsers') }}
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-row justify="center" class="py-6" :style="{ visibility: loading ? undefined : 'hidden' }">
            <platform-spinner />
          </v-row>
        </v-col>
        <v-col :cols="12" :lg="4">
          <v-card>
            <v-card-title>{{ $t('page.organisationUsers.header') }}</v-card-title>
            <v-card-subtitle>{{ $t('page.organisationUsers.description') }}</v-card-subtitle>
            <v-card-actions>
              <platform-button
                primary
                @click="addingUser = true"
              >
                {{ $t('page.organisationUsers.addUserAction') }}
              </platform-button>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </platform-infinite-scroll>
</template>

<script lang="ts">
import PlatformInfiniteScroll from '@/components/shared/PlatformInfiniteScroll.vue'
import PlatformSpinner from '@/components/shared/PlatformSpinner.vue'
import PlatformEntityCard from '@/components/PlatformEntityCard.vue'
import PlatformButton from '@/components/shared/PlatformButton.vue'
import PlatformAvatar from '@/components/shared/PlatformAvatar.vue'
import AddOrganisationUserModal from '@/components/organisation/AddOrganisationUserModal.vue'
import EditOrganisationUserModal from '@/components/organisation/EditOrganisationUserModal.vue'
import Vue from 'vue'
import API from '@/services/API'
import dayjs from 'dayjs'
import { getAllOrganisationUserAccess } from '@/helpers/organisationAccess'

interface OrganisationUserWithDetails {
  id: string
  organisationId: string
  cognitoIdentityId: string
  createdAt: string
  updatedAt: string
  user: {
    id: string
    cognitoId: string | null
    displayName: string | null
    email: string | null
  } | null
}

interface OrganisationInfo {
  id: string
  name: string | null
}

export default Vue.extend({
  name: 'OrganisationUserManagement',
  components: {
    PlatformInfiniteScroll,
    PlatformSpinner,
    PlatformEntityCard,
    PlatformButton,
    PlatformAvatar,
    AddOrganisationUserModal,
    EditOrganisationUserModal
  },
  data () {
    return {
      organisationUsers: [] as OrganisationUserWithDetails[],
      organisations: [] as OrganisationInfo[],
      userOrganisationIds: [] as string[],
      editingOrganisationUser: null as OrganisationUserWithDetails | null,
      addingUser: false,
      loading: false
    }
  },
  mounted () {
    void this.fetchData()
  },
  methods: {
    async fetchData (): Promise<void> {
      this.loading = true

      let userOrgIds: string[] = []
      try {
        const { organisationUsers: userOrgUsers } = await getAllOrganisationUserAccess()
        userOrgIds = [...new Set(userOrgUsers.map(ou => ou.organisationId))]
      } catch (error) {
        console.error('Failed to fetch user organisation access:', error)
        this.$toasted.error(this.$t('page.organisationUsers.fetchError') as string)
        this.loading = false
        return
      }

      if (userOrgIds.length === 0) {
        this.organisationUsers = []
        this.userOrganisationIds = []
        this.organisations = []
        this.loading = false
        return
      }

      let response: { organisationUsers?: OrganisationUserWithDetails[], organisations?: OrganisationInfo[] }
      try {
        response = await API.post('backendfunctions', '/organisation/dashboard', {
          body: {
            action: 'getOrganisationUsers'
          }
        })
      } catch (error) {
        console.error('Failed to fetch organisation users:', error)
        this.$toasted.error(this.$t('page.organisationUsers.fetchError') as string)
        this.loading = false
        return
      }

      this.organisationUsers = response.organisationUsers ?? []
      const allOrganisations = response.organisations ?? []

      this.organisations = allOrganisations.filter(org => org.name)

      const validOrgIds = new Set(this.organisations.map(o => o.id))
      this.userOrganisationIds = userOrgIds.filter(id => validOrgIds.has(id))

      this.loading = false
    },
    getOrganisationUsersForOrg (organisationId: string): OrganisationUserWithDetails[] {
      return this.organisationUsers.filter(ou => ou.organisationId === organisationId)
    },
    getOrganisationName (organisationId: string): string {
      const org = this.organisations.find(o => o.id === organisationId)
      if (!org?.name) {
        console.error('Organisation name not found for ID:', organisationId)
        return ''
      }
      return org.name
    },
    formatUserEntity (orgUser: OrganisationUserWithDetails): { id: string, displayName?: string, email?: string } {
      return {
        id: orgUser.cognitoIdentityId,
        displayName: orgUser.user?.displayName ?? undefined,
        email: orgUser.user?.email ?? undefined
      }
    },
    formatDate (dateString: string): string {
      return dayjs(dateString).format('DD MMM YYYY')
    },
    userAdded (): void {
      this.$toasted.success(this.$t('page.organisationUsers.userAddedSuccess') as string)
      void this.fetchData()
    },
    userRemoved (): void {
      this.$toasted.success(this.$t('page.organisationUsers.userRemovedSuccess') as string)
      void this.fetchData()
    }
  }
})
</script>
